import React, { useState } from 'react';
import { Wand2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../../store';
import { translateLocalizedValue, canTranslate } from '../../lib/translateLocalized';

/**
 * Form-level "Translate ALL Fields" master button.
 *
 * Looks at every LocalizedField on the form, batches the empty NL/RO/RU tabs,
 * fires concurrent /api/admin/translate calls (Claude Sonnet 4.5) and fills
 * them all in one click. Existing translations are never overwritten.
 *
 * Pass `fields` as a function returning [{ value, onChange }] so it is read
 * at click time. IMPORTANT: each `onChange` MUST use functional setState
 * (e.g. setForm(p => ({ ...p, x }))) so concurrent updates don't clobber
 * each other.
 */
const TranslateAllButton = ({ fields, testid = 'translate-all', className = '' }) => {
  const token = useAuthStore((s) => s.token);
  const [busy, setBusy] = useState(false);

  const resolve = () => (typeof fields === 'function' ? fields() : fields) || [];

  const handle = async () => {
    const list = resolve();
    const translatable = list.filter((f) => canTranslate(f.value));
    if (!token) { toast.error('Not authenticated'); return; }
    if (translatable.length === 0) {
      toast.info('Nothing to translate — fill English text or all languages already complete');
      return;
    }
    setBusy(true);
    let totalFilled = 0;
    let fieldsTouched = 0;
    try {
      const results = await Promise.allSettled(
        translatable.map((f) => translateLocalizedValue(token, f.value).then((r) => ({ r, f }))),
      );
      let hadError = null;
      results.forEach((res) => {
        if (res.status === 'fulfilled') {
          const { r, f } = res.value;
          if (r.filled > 0) {
            f.onChange(r.value);
            totalFilled += r.filled;
            fieldsTouched += 1;
          }
        } else {
          hadError = res.reason;
        }
      });
      if (totalFilled > 0) {
        toast.success(`Translated ${fieldsTouched} field(s) · ${totalFilled} language slot(s) filled`);
      } else if (hadError) {
        toast.error(hadError.message || 'Translate all failed');
      } else {
        toast.info('Nothing to translate');
      }
      if (hadError && totalFilled > 0) {
        toast.warning('Some fields could not be translated');
      }
    } catch (e) {
      toast.error(e.message || 'Translate all failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {busy && (
        <div
          className="fixed inset-0 z-[200] bg-background/70 backdrop-blur-sm flex items-center justify-center"
          data-testid={`${testid}-overlay`}
        >
          <div className="flex items-center gap-3 bg-foreground text-background px-6 py-4 text-sm tracking-wide">
            <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
            Translating all fields…
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={handle}
        disabled={busy}
        title="Translate all empty fields into NL · RO · RU"
        className={`inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-[0.2em] uppercase border transition-colors ${
          busy
            ? 'border-border text-muted-foreground/60 cursor-wait'
            : 'border-foreground text-foreground hover:bg-foreground hover:text-background cursor-pointer'
        } ${className}`}
        data-testid={testid}
      >
        {busy
          ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
          : <Wand2 className="w-4 h-4" strokeWidth={1.5} />}
        Translate ALL
      </button>
    </>
  );
};

export default TranslateAllButton;
